import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiPlus, PiTrash } from "react-icons/pi";
import { ArrowRight, User, Calendar } from "lucide-react";
import OvanBlog from "../../ovan/ovanblog/OvanBlog";

const BLOG_IMAGES = {
  big: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800&h=500&fit=crop",
  small: [
    "https://images.unsplash.com/photo-1519452635265-7b1fbfd1e4e0?w=400&h=300&fit=crop",
    "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=400&h=300&fit=crop",
    "https://images.unsplash.com/photo-1562774053-701939374585?w=400&h=300&fit=crop",
  ],
};

export default function UniversityBlog({ content, onContentChange, isSelected }: SectionContentProps) {
  if (content?.variant === "ovan") {
    return <OvanBlog content={content} onContentChange={onContentChange} isSelected={isSelected} />;
  }

  const safeContent = content || {};
  const posts = safeContent.posts || [
    { title: "Those inequalities are inequalities that occur within households", author: "Samira Khan", date: "November 28, 2023", link: "#" },
    { title: "Those inequalities are inequalities that occur within households", author: "Zent Ekizie", date: "November 28, 2023", link: "#" },
    { title: "After decades of improvement, cardiovascular health rates", author: "Zayed Khan", date: "November 28, 2023", link: "#" },
  ];

  const bigPost = safeContent.bigPost || {
    title: "Classroom community-building — It's ok to try something new!",
    author: "Masum Sakib",
    date: "November 28, 2023",
    link: "#",
  };

  const addPost = () => {
    const newPosts = [...posts, { title: "New Post", author: "Author", date: "Jan 1, 2024", link: "#" }];
    onContentChange?.({ ...safeContent, posts: newPosts });
  };

  const removePost = (idx: number) => {
    const newPosts = posts.filter((_: any, i: number) => i !== idx);
    onContentChange?.({ ...safeContent, posts: newPosts });
  };

  const updatePost = (idx: number, field: string, value: string) => {
    const newPosts = [...posts];
    newPosts[idx] = { ...newPosts[idx], [field]: value };
    onContentChange?.({ ...safeContent, posts: newPosts });
  };

  return (
    <section className="bg-[#F3F6FF] py-20 px-4">
      <div className="mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="mb-10 flex flex-wrap items-end justify-between border-b border-gray-200 pb-6">
          <div>
            <h2 className="mb-2 text-3xl font-bold uppercase tracking-tight text-[#1A1A1A]">
              <EditableText
                value={safeContent.title || "Latest Blogs"}
                onChange={(val) => onContentChange?.({ ...safeContent, title: val })}
                as="span"
                placeholder="Section Title"
              />
            </h2>
            <p className="text-[#6c7279]">
              <EditableText
                value={safeContent.subtitle || "Whether you are considering a foundation course or an undergraduate academics is a place."}
                onChange={(val) => onContentChange?.({ ...safeContent, subtitle: val })}
                as="span"
                placeholder="Subtitle"
              />
            </p>
          </div>
          <a
            href={safeContent.viewAllLink || "#"}
            className="group mt-4 flex items-center gap-2 text-sm font-medium text-[#FF6D1C] hover:text-[#e55a0f] transition-colors lg:mt-0"
          >
            View All
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Big Post */}
          <div className="group">
            <div className="overflow-hidden rounded-xl bg-white shadow-md transition-all hover:shadow-xl">
              <a href={bigPost.link || "#"} className="block">
                <img
                  src={safeContent.bigImage || BLOG_IMAGES.big}
                  alt={bigPost.title}
                  className="h-72 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </a>
              <div className="p-6">
                <div className="mb-3 flex flex-wrap gap-4 text-sm text-[#6c7279]">
                  <span className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    <EditableText
                      value={bigPost.author}
                      onChange={(val) => onContentChange?.({ ...safeContent, bigPost: { ...bigPost, author: val } })}
                      as="span"
                      placeholder="Author"
                    />
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <EditableText
                      value={bigPost.date}
                      onChange={(val) => onContentChange?.({ ...safeContent, bigPost: { ...bigPost, date: val } })}
                      as="span"
                      placeholder="Date"
                    />
                  </span>
                </div>
                <a href={bigPost.link || "#"} className="block">
                  <h3 className="text-xl font-bold text-[#1A1A1A] hover:text-[#FF6D1C] transition-colors">
                    <EditableText
                      value={bigPost.title}
                      onChange={(val) => onContentChange?.({ ...safeContent, bigPost: { ...bigPost, title: val } })}
                      as="span"
                      placeholder="Post Title"
                    />
                  </h3>
                </a>
              </div>
            </div>
          </div>

          {/* Small Posts */}
          <div className="flex flex-col gap-4">
            {posts.map((post: any, idx: number) => (
              <div
                key={idx}
                className="group flex gap-4 rounded-xl bg-white p-4 shadow-md transition-all hover:shadow-xl"
                style={{ position: "relative" }}
              >
                {isSelected && (
                  <button
                    onClick={() => removePost(idx)}
                    className="absolute -top-2 -right-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600"
                  >
                    <PiTrash className="h-3 w-3" />
                  </button>
                )}

                {/* Thumbnail */}
                <a href={post.link || "#"} className="h-24 w-32 flex-shrink-0 overflow-hidden rounded-lg">
                  <img
                    src={BLOG_IMAGES.small[idx % BLOG_IMAGES.small.length]}
                    alt={post.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </a>

                {/* Content */}
                <div className="flex-1">
                  <div className="mb-2 flex flex-wrap gap-4 text-xs text-[#6c7279]">
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      <EditableText
                        value={post.author}
                        onChange={(val) => updatePost(idx, "author", val)}
                        as="span"
                        placeholder="Author"
                      />
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <EditableText
                        value={post.date}
                        onChange={(val) => updatePost(idx, "date", val)}
                        as="span"
                        placeholder="Date"
                      />
                    </span>
                  </div>
                  <a href={post.link || "#"} className="block">
                    <h4 className="text-sm font-bold text-[#1A1A1A] leading-snug hover:text-[#FF6D1C] transition-colors">
                      <EditableText
                        value={post.title}
                        onChange={(val) => updatePost(idx, "title", val)}
                        as="span"
                        placeholder="Post Title"
                      />
                    </h4>
                  </a>
                </div>
              </div>
            ))}

            {isSelected && (
              <button
                onClick={addPost}
                className="flex h-24 items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 hover:border-[#FF6D1C] hover:bg-orange-50 hover:text-[#FF6D1C] transition-all"
              >
                <PiPlus className="h-8 w-8 mr-2" />
                <span className="text-sm font-medium">Add Blog Post</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
